// A project in its rooms, laid out like the Mac's RoomShell: the top bar with the five rooms (Plan · Research · Write ·
// Revise · Publish), the desk with the left column (the project's name back to the Library, then the room's column:
// the contents, the Library's projects, or a page's own, such as the story pages' people and places), and the page on a paper panel
// under its room's tabs (RoomTabHeader: the room's pages at the left, the page's controls at the right), with the
// inspector on the right (⌥⌘I) for pages about an item. Which page shows is the registry's (rooms/registry.ts): the
// shell knows no page by name.
//
// Keys (Mac first; Ctrl stands for ⌘ elsewhere): ⌥⌘1–⌥⌘5 the rooms, ⌥⌘0 the Library, ⌃⌘S (Ctrl+Alt+S) the column,
// ⌥⌘I the inspector, ⌘S a checkpoint, ⇧⌘F Find in Project, ⌥⌘↓/⌥⌘↑ Next and Previous Document (through the contents'
// filter's matches: the filter bar heads the contents, and the pages are given the tree it shows). At 700 px and
// narrower the column becomes a drawer and the rooms sit along the bottom (PhoneRoomBar), with a page's controls on a
// second row under its tabs; at 900 px and narrower the inspector is a sheet over the page.
//
// Two sides of the contents (`showsResearch`): Research shows the reference arrangements (Research & Notes), Write,
// Revise and Publish the manuscript, its alternates, Publishing and Trash; Plan either (`showsEitherSide`: its side is
// its arrangement's, and its picker offers them all). Each side keeps the arrangement and item it last showed
// (`sideMemory`), so a room tab across to the other side lands where the writer left it, and a URL whose `?in=` is
// on the wrong side for its room is sent to the right room (`/write/<n>?in=<research>` opens in Research).
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { SCRIPT_FORMATS, scriptFormatName, StoreEditor, type ArrangementInfo, type DocumentEditor, type OpenedProject, type Project, type TreeNode } from '@writing-suite/core';
import type { OutboxStatus } from '@writing-suite/client/net/outbox.ts';
import { quotaMessage } from '@writing-suite/client/browser/usage.ts';
import { projectTitle, roleOf } from '../store/library.ts';
import { setOpenProject } from '../store/storageId.ts';
import { rotateKey, type RotationProgress } from '../store/sharing.ts';
import { ShareDialog, rotationText } from '../views/ShareDialog.tsx';
import { LeaveDialog, RemovedBanner } from '../views/SharedProject.tsx';
import type { LiveProject } from '../store/project.ts';
import { useLiveProject } from '../store/useProject.ts';
import { FindPanel } from '../views/FindPanel.tsx';
import { ReaderLinks } from '../store/readers.ts';
import { TextDialog } from '../views/Margin.tsx';
import { MenuButton } from '../views/Menu.tsx';
import { SprintDialog, SprintSummary } from '../views/Sprint.tsx';
import { Writing } from '../store/writing.ts';
import { findNode } from '../views/helpers.ts';
import { Inspector } from '../views/Inspector.tsx';
import { useLookUp } from '../store/lookUp.ts';
import { ConfirmDialog } from '../views/Menu.tsx';
import { useScappleImport } from '../views/maps/ScappleImport.tsx';
import { fragmentsOf, isWithin, locate, type StructureAsk } from '../views/structure.ts';
import { registry, type RoomPage } from '../rooms/registry.ts';
import { roomDef, ROOMS, showsEitherSide, showsResearch, WORKING_ROOMS, type RoomId } from '../rooms/rooms.ts';
import { ContentsTree } from './ContentsTree.tsx';
import { ProjectsColumn } from './Columns.tsx';
import { announce, useApp, usePreference } from './context.ts';
import { markAfterPaint, perfMark, perfMeasure } from './perf.ts';
import { roomMenuEntries, roomSheet, type MenuContext } from '../rooms/menus.ts';
import { AccountLinks } from './AccountLinks.tsx';
import { followLink, formatRoute, navigate, rememberPage, rememberRoom, resolvePage, type ProjectRoute } from './route.ts';
import { structureEdits, WorkspaceContext, WorkspaceRefContext, type RenameSite, type RevealTarget, type Sheet, type Workspace } from './workspace.ts';
import { nodeOfFragment } from '../views/structure.ts';
import { chapterNumbers, collectionOf, locksOf, rowFactsOf, sameRowFacts, type RowFacts } from '../store/planModel.ts';
import { planEdit, PREF, useSharedPreference } from '../store/plan.ts';
import { FilterBar, FilterFoot, FilterList } from '../views/FilterBar.tsx';
import { useBinderFilter } from './useBinderFilter.ts';
import { SourcePicker } from '../views/plan/SourcePicker.tsx';
import { ScrivenerReimportDialog } from '../views/plan/ScrivenerReimport.tsx';
import { useAccountName } from '../views/Home.tsx';
import { convertedMessage, convertLinkComments, linkCommentCount, linkCommentsOffered, markLinkCommentsOffered } from '../store/linkComments.ts';

const SHOWN_ROLES: ArrangementInfo['role'][] = ['manuscript', 'reference', 'publishing', 'alternate', 'trash'];
const ROLE_ORDER = (a: ArrangementInfo) => SHOWN_ROLES.indexOf(a.role);

/** What every room page (and a page's own column and header controls) is given. */
export interface PageProps {
  /** The project's id on the relay (the web's; a copy's may differ from its document's own id). */
  projectId: string;
  project: Project;
  tree: TreeNode[];
  node: TreeNode | null;
  arrangement: ArrangementInfo;
  /**
   * A link to a page: "room/page" (`'plan/corkboard'`), a room (its last page), or an old page name ('write', 'cards'…),
   * with a node when the page is about one (the arrangement is kept) and query parameters for the page to read.
   */
  hrefFor(page: string, nodeId: string | null, params?: Record<string, string>): string;
  /** The live project, for edits (null while it opens). */
  live: LiveProject | null;
  /** The page shown (its registry entry). */
  page: RoomPage;
  /** The URL's other query parameters (a column's choice). */
  params: Record<string, string>;
}

/** How this device's edits stand, in the writer's words (the Mac's footer: "Saving…", "Saved"). */
export function saveState(save: OutboxStatus | null, online: boolean): { state: string; text: string; detail?: string } | null {
  if (!save) return null;
  const waiting = save.pending + save.unsaved;
  const changes = waiting === 1 ? '1 change' : `${waiting.toLocaleString('en-GB')} changes`;
  // The account is full (PROTOCOL.md §9's `quota`): the changes stay here, and the banner says what to do.
  const quota = save.refused.find((r) => r.code === 'quota');
  if (quota) return { state: 'quota', text: 'Account full — changes kept here', detail: quotaMessage({ limit: quota.limit, used: quota.used }, { saving: true }) };
  if (save.refused.length) {
    const r = save.refused[0];
    return { state: 'refused', text: `Couldn’t save ${changes}: the server refused them (${r.code}). They’re kept on this computer.` };
  }
  if (!online || !save.connected) {
    if (!waiting) return save.lastAckAt ? { state: 'saved', text: 'Saved' } : null;
    return save.unsaved ? { state: 'saving', text: 'Saving on this computer…' } : { state: 'kept', text: `Offline — changes kept on this computer (${changes} to send)` };
  }
  if (waiting || save.sending) return { state: 'saving', text: 'Saving…' };
  return save.lastAckAt ? { state: 'saved', text: 'Saved' } : null;
}

/** The room a key chord asks for (⌥⌘1–5, ⌥⌘0; Ctrl+Alt elsewhere, never AltGr, which types characters). */
export function roomForKey(e: Pick<KeyboardEvent, 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey' | 'code'> & { getModifierState?(k: string): boolean }): RoomId | null {
  if (!(e.metaKey || e.ctrlKey) || !e.altKey || e.shiftKey || e.getModifierState?.('AltGraph')) return null;
  const m = /^(?:Digit|Numpad)(\d)$/.exec(e.code);
  return m ? (ROOMS.find((r) => r.key === m[1])?.id ?? null) : null;
}

/** A room's grouped tabs (rooms.ts `group`), each with its pages in order. */
/** Plan pages a journal doesn't have: the beat board and the fiction story pages. */
const JOURNAL_HIDDEN_PAGES = new Set(['beats', 'timeline', 'study', 'chronology', 'continuity']);

function tabGroups(tabs: RoomPage[]): [string, RoomPage[]][] {
  const out = new Map<string, RoomPage[]>();
  for (const p of tabs) if (p.group) out.set(p.group, [...(out.get(p.group) ?? []), p]);
  return [...out];
}

/** ⌃⌘S (Ctrl+Alt+S elsewhere): the column. */
export const isColumnKey = (e: Pick<KeyboardEvent, 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey' | 'code'>) =>
  e.code === 'KeyS' && e.ctrlKey && (e.metaKey || e.altKey) && !e.shiftKey;

export function ProjectShell({ route }: { route: ProjectRoute }) {
  const app = useApp();
  const open = useLiveProject(app, route.projectId);
  const project = open.project;
  // Shared projects (W9a): this account's role, and whose project it is.
  const listedEntry = app.projects?.find((p) => p.id === route.projectId) ?? null;
  const role = roleOf(listedEntry);
  const ownerName = listedEntry?.shared?.ownerName || null;
  const closedShare = open.removed || (!!listedEntry?.shared && listedEntry.shared.status !== 'active');
  const [sharingOpen, setSharingOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [rotation, setRotation] = useState<RotationProgress | null>(null);
  // Per-browser things keyed by project (line spacing, the session…) use this project's address (store/storageId.ts).
  useEffect(() => {
    if (project) setOpenProject(project.id, route.projectId);
  }, [project?.id, route.projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => setOpenProject(null), []);
  const reg = registry();
  const page = reg.find(route.room, route.page) ?? reg.first(route.room);
  const room = roomDef(route.room);
  const [drawer, setDrawer] = useState(false);
  const pageRef = useRef<HTMLElement>(null);
  // Each rebuild drawn (`ws:render`: from this render starting to its commit), then painted (only when measuring).
  const renderStart = performance.now();
  useLayoutEffect(() => {
    perfMeasure('ws:render', renderStart, open.version);
    perfMark('ws:commit', open.version);
    markAfterPaint(open.version);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open.version]);
  const toggleRef = useRef<HTMLButtonElement>(null);
  // A link in a page's own column (Story's people and places) closes the phone's drawer, as the contents' rows do.
  const paramsKey = JSON.stringify(route.params);
  useEffect(() => setDrawer(false), [route.room, route.page, route.nodeId, paramsKey]);

  // A room returns to the page it last showed (hidden pages, reached from menus, aren't remembered).
  useEffect(() => {
    if (!page.hidden) rememberPage(route.room, page.page);
  }, [route.room, page.page, page.hidden]);
  // The room the Library goes back to, and the one this project opens in next time (`/p/<project>`).
  const [lastWorkingRoom, setLastWorkingRoom] = usePreference<RoomId>('lastRoom', 'write');
  useEffect(() => {
    if (route.room !== 'library' && route.room !== lastWorkingRoom) setLastWorkingRoom(route.room);
    if (route.room !== 'library') rememberRoom(route.projectId, route.room);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.room, route.projectId]);
  const arrangements = useMemo(
    () => (project ? project.arrangements().filter((a) => SHOWN_ROLES.includes(a.role)).sort((a, b) => ROLE_ORDER(a) - ROLE_ORDER(b)) : []),
    [project],
  );
  const requested = arrangements.find((a) => a.id === route.arrangementId) ?? null;
  const either = showsEitherSide(route.room);
  const research = either ? requested?.role === 'reference' : showsResearch(route.room);
  // Collections (saved searches) are filters: choosing one loads its facets into the filter bar (store/binderFilter.ts).
  const opened = project && (project as OpenedProject).record ? (project as OpenedProject) : null;
  const locks = useMemo(() => (project ? locksOf(project) : new Set<string>()), [project]);
  const numbers = useMemo(() => (project ? chapterNumbers(project) : new Map<string, string>()), [project]);
  // This side's arrangements: the reference ones in Research, the rest elsewhere.
  const sideArrangements = useMemo(() => arrangements.filter((a) => (a.role === 'reference') === research), [arrangements, research]);
  const arrangement =
    (requested && sideArrangements.includes(requested) ? requested : null)
      ?? (research ? null : arrangements.find((a) => a.id === project?.manuscriptId()))
      ?? sideArrangements[0]
      ?? null;
  const inManuscript = !!arrangement && arrangement.id === project?.manuscriptId();
  // An arrangement on the other side, asked for by a page about an item: the room on its side shows it.
  const wrongSide = !either && !!requested && (requested.role === 'reference') !== research && page.takesNode;
  useEffect(() => {
    if (!wrongSide || !requested) return;
    const room: RoomId = requested.role === 'reference' ? 'research' : 'write';
    navigate(formatRoute({ name: 'project', projectId: route.projectId, room, page: reg.first(room).page, nodeId: route.nodeId, arrangementId: requested.id, params: route.params }), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrongSide, requested?.id]);
  // Where each side's contents were (the arrangement and the item), kept while the project is open.
  const sideMemory = useRef<Record<string, { arrangementId: string | null; nodeId: string | null }>>({});
  useEffect(() => {
    sideMemory.current = {};
  }, [route.projectId]);
  useEffect(() => {
    if (route.room === 'library' || page.column || wrongSide) return;
    sideMemory.current[String(research)] = { arrangementId: route.arrangementId, nodeId: page.takesNode ? route.nodeId : null };
  });
  // The tree keeps its identity while nothing in it changed (typing rebuilds the project, not the contents).
  const built = useMemo(() => (project && arrangement ? project.tree(arrangement.id) : []), [project, arrangement?.id]);
  const stableTree = useRef<TreeNode[]>(built);
  if (stableTree.current !== built && !sameTree(stableTree.current, built)) stableTree.current = built;
  const fullTree = stableTree.current;
  // The contents' filter: the pages are given the tree it shows (the matches, and the folders above them).
  const binder = useBinderFilter({
    project, projectKey: route.projectId, arrangement, fullTree, selected: route.nodeId, locks,
    select: (nodeId) => navigate(hrefFor(pageForNode, nodeId)),
    show: (role) => {
      const to = role === 'manuscript' ? null : (arrangements.find((a) => a.role === role)?.id ?? null);
      if (role !== 'manuscript' && !to) return;
      // A research collection opens in Research, a manuscript one in Write (or the room already on that side, or Plan).
      const toResearch = role === 'reference';
      const room: RoomId = either || toResearch === research ? route.room : toResearch ? 'research' : 'write';
      navigate(formatRoute({ name: 'project', projectId: route.projectId, room, page: room === route.room ? route.page : reg.first(room).page, nodeId: null, arrangementId: to, params: {} }));
    },
  });
  const filter = binder.filter;
  const filtering = !!filter?.view;
  // A collection belongs to one side: a research collection left behind in Write would filter the manuscript by it.
  const loadedScope = filter?.saved?.scope ?? null;
  useEffect(() => {
    if ((loadedScope === 'research' || loadedScope === 'manuscript') && (loadedScope === 'research') !== research) filter?.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [research]);
  const tree = binder.tree;
  const node = binder.node;
  // With the filter on, the contents hide what it leaves out, or draw it faded (the contents' foot; per browser).
  const [dimsFilteredOut] = useSharedPreference<boolean>(PREF.binderDims, false);
  const dims = dimsFilteredOut && filtering && !filter?.state.list;
  // Each document's words, label colour and kind for its row, brought up to date a moment after the project changes: the
  // memoized contents re-render once typing stops, never on every key.
  const [rowFacts, setRowFacts] = useState<RowFacts>(() => new Map());
  useEffect(() => {
    if (!opened) return;
    const t = setTimeout(() => setRowFacts((old) => {
      const next = rowFactsOf(opened);
      return sameRowFacts(old, next) ? old : next;
    }), rowFacts.size ? 600 : 0);
    return () => clearTimeout(t);
  }, [opened]); // eslint-disable-line react-hooks/exhaustive-deps
  // A link to a collection (from before collections were filters): its facets load into the bar over the manuscript.
  useEffect(() => {
    if (!opened || !filter || !route.arrangementId || arrangements.some((a) => a.id === route.arrangementId)) return;
    const search = collectionOf(opened, route.arrangementId);
    if (!search) return;
    filter.load(search);
    navigate(formatRoute({ name: 'project', projectId: route.projectId, room: route.room, page: route.page, nodeId: null, arrangementId: null, params: route.params }), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.arrangementId, !!opened]);

  const hrefFor = (ref: string, nodeId: string | null, params?: Record<string, string>) => {
    const to = resolvePage(ref) ?? { room: 'write' as RoomId, page: 'write' };
    // Across to the other side with nothing named: where that side's contents were. With an item named, the item's
    // own arrangement (this one) goes with it, and the shell sends the page to the right room if it's across.
    const crossing = showsResearch(to.room) !== research && to.room !== 'library' && !showsEitherSide(to.room);
    const memory = crossing && !nodeId ? sideMemory.current[String(showsResearch(to.room))] : null;
    const takesNode = !!reg.find(to.room, to.page)?.takesNode;
    return formatRoute({
      name: 'project', projectId: route.projectId, room: to.room, page: to.page,
      nodeId: nodeId ?? (memory && takesNode ? memory.nodeId : null),
      arrangementId: crossing && !nodeId ? (memory?.arrangementId ?? null) : inManuscript ? null : (arrangement?.id ?? null),
      params: params ?? {},
    });
  };
  /** The editor rooms: Write over the manuscript's side, Research over the reference arrangements. */
  const editing = route.room === 'write' || route.room === 'research';
  /** The editor page for an arrangement: Research's for a reference arrangement, else Write's. */
  const editorFor = (arrangementId: string | null) => (arrangements.find((a) => a.id === arrangementId)?.role === 'reference' ? 'research/research' : 'write/write');
  /** A room's tab: its last page, keeping the item when that page is about one. */
  const roomHref = (id: RoomId) => {
    const to = resolvePage(id)!;
    return hrefFor(`${to.room}/${to.page}`, reg.find(to.room, to.page)?.takesNode ? route.nodeId : null);
  };
  /** The Library, and back from it to the room last worked in. */
  const libraryHref = roomHref('library');
  const backHref = roomHref(lastWorkingRoom === 'library' ? 'write' : lastWorkingRoom);

  // What the pages share: the caret's fragment (the inspector follows it), the item being renamed, the editor's focus.
  const [caretFragment, setCaretFragment] = useState<string | null>(null);
  const [renamingState, setRenamingState] = useState<{ id: string; in: RenameSite } | null>(null);
  const renaming = renamingState?.id ?? null;
  const setRenaming = useCallback((id: string | null, where: RenameSite = 'contents') => setRenamingState(id ? { id, in: where } : null), []);
  const [trashing, setTrashing] = useState<string[] | null>(null);
  const [structureAsk, setStructureAsk] = useState<StructureAsk | null>(null);
  const editorFocus = useRef<(() => void) | null>(null);
  const [inspectorPref, setInspectorPref] = usePreference('inspector', false);
  const [columnPref, setColumnPref] = usePreference('leftColumn', true);
  const narrow = useNarrow('(max-width: 900px)');
  const phone = useNarrow('(max-width: 700px)');
  // On a narrow screen the inspector is a sheet over the page: it opens only when asked for there (or when the window
  // narrows with the panel showing), never by itself on load, and once closed it stays closed.
  const [sheetOpen, setSheetOpen] = useState(false);
  const wasNarrow = useRef(narrow);
  useEffect(() => {
    if (narrow && !wasNarrow.current && inspectorPref) setSheetOpen(true);
    if (!narrow) setSheetOpen(false);
    wasNarrow.current = narrow;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [narrow]);
  const inspectorOpen = narrow ? sheetOpen : inspectorPref;
  const setInspectorOpen = (on: boolean) => {
    if (narrow) {
      setSheetOpen(on);
      if (!on) setInspectorPref(false);
    } else setInspectorPref(on);
  };
  // Look Up (the text's menu, Cast & Places): each explicit look-up brings the inspector into view on its tab.
  const { requests: lookUpRequests, tab: lookUpTab } = useLookUp();
  useEffect(() => {
    if (lookUpRequests) setInspectorOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookUpRequests]);
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const accountName = useAccountName();
  const [revealing, setRevealing] = useState<RevealTarget | null>(null);
  const [shownFragments, setShownFragments] = useState<string[]>([]);

  // This browser's session and sprint (per project); each save's words counted if they're in the counted text.
  const [writing, setWriting] = useState<Writing | null>(null);
  const projectRef = useRef(project);
  projectRef.current = project;
  useEffect(() => {
    const live = open.live;
    if (!live || !project) return;
    const w = new Writing(project.id, {
      projectTitle: () => projectRef.current?.title ?? '',
      words: () => projectRef.current?.manuscriptWords() ?? 0,
      flush: () => live.flush(),
    });
    setWriting(w);
    const off = live.onWordsSaved((fid, before, after) => {
      const p = projectRef.current;
      if (p && p.counted(p.targets().countAllText).includes(fid)) w.wordsSaved(before, after);
    });
    return () => {
      off();
      w.dispose();
      setWriting(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open.live, !!project]);
  const writable = !!open.live?.writable;
  // Readers' notes from web links are placed as comments once the project has caught up with the relay (then on demand,
  // Revise ▸ Readers). Only when the project has rounds: nothing is asked of the relay otherwise.
  const placedFor = useRef<LiveProject | null>(null);
  useEffect(() => {
    const live = open.live;
    if (!live || !writable || role !== 'owner' || open.connection !== 'live' || !app.online || placedFor.current === live) return;
    placedFor.current = live;
    const links = new ReaderLinks(app.account.session.api, live);
    if (!links.rounds().length) return;
    void links
      .place()
      .then((r) => {
        if (r.added) announce(`${r.added} ${r.added === 1 ? 'note' : 'notes'} from readers placed as comments.`);
      })
      .catch(() => {
        placedFor.current = null; // tried again when the connection returns
      });
  }, [open.live, writable, role, open.connection, app.online, app.account.session.api]);
  // Once per project in this browser, when it has caught up: link comments an importer made before links existed are
  // offered as links (the Mac's `offerLinkConversionIfNeeded`).
  const [offeringLinks, setOfferingLinks] = useState<number | null>(null);
  const linksOfferedFor = useRef<LiveProject | null>(null);
  useEffect(() => {
    const live = open.live;
    if (!live || !project || !writable || role === 'commenter' || open.connection !== 'live' || linksOfferedFor.current === live) return;
    linksOfferedFor.current = live;
    if (linkCommentsOffered(project.id)) return;
    const count = linkCommentCount(live, project);
    markLinkCommentsOffered(project.id);
    if (count > 0) setOfferingLinks(count);
  }, [open.live, project, writable, role, open.connection]);
  const scapple = useScappleImport({ live: open.live, projectId: route.projectId, mapsHref: hrefFor('plan/maps', null) });
  const turnLinkCommentsIntoLinks = () => {
    if (!open.live) return;
    announce(convertedMessage(convertLinkComments(open.live)));
  };
  // A rotation this browser began and didn't finish, or one the relay asks for (a member removed or left): the owner's
  // project changes its key once it's caught up (PROTOCOL.md §10.7).
  const rotatedFor = useRef<LiveProject | null>(null);
  useEffect(() => {
    const live = open.live;
    if (!live || role !== 'owner' || !listedEntry || open.connection !== 'live' || !app.online || rotatedFor.current === live) return;
    rotatedFor.current = live;
    void (async () => {
      const due = !!(await app.account.store.rotation(listedEntry.id)) || !!listedEntry.needsRotation;
      if (!due) return;
      try {
        setRotation({ step: 'key', done: 0, total: 0 });
        await rotateKey({ account: app.account, projectId: listedEntry.id, key: live.key, live, summary: listedEntry.summary, onProgress: setRotation });
        announce('The project’s key has changed');
        void app.refreshProjects();
      } catch {
        rotatedFor.current = null; // tried again when the connection returns
      } finally {
        setTimeout(() => setRotation(null), 4000);
      }
    })();
  }, [open.live, role, listedEntry, open.connection, app.online]); // eslint-disable-line react-hooks/exhaustive-deps
  const canWrite = writable && !!arrangement && arrangement.role !== 'trash';
  // The filter's list reads only, as collections did (moving among the matches is in the tree).
  const canStructure = canWrite && !!arrangement && arrangement.role !== 'collection' && !(filtering && filter?.state.list);
  /** The page an item opens on: this one when it's about an item, else Write. */
  const pageForNode = page.takesNode ? `${page.room}/${page.page}` : 'write/write';
  const openNode = useCallback(
    (nodeId: string | null, ref?: string) => navigate(hrefFor(ref ?? pageForNode, nodeId)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [route.projectId, arrangement?.id, inManuscript, pageForNode],
  );
  const workspace: Workspace | null =
    project && arrangement
      ? {
          project,
          live: open.live,
          arrangement,
          tree,
          fullTree,
          filter,
          showColumn: () => {
            if (phone) setDrawer(true);
            else setColumnPref(true);
            requestAnimationFrame(() => document.querySelector<HTMLElement>('#contents .filter-input')?.focus());
          },
          node,
          canWrite,
          canStructure,
          canComment: !!open.live?.canComment && arrangement.role !== 'trash' && !closedShare,
          role,
          ownerName,
          caretFragment,
          setCaretFragment,
          renaming,
          renamingIn: renamingState?.in ?? null,
          setRenaming,
          focusEditor: () => editorFocus.current?.(),
          setEditorFocus: (fn) => (editorFocus.current = fn),
          open: openNode,
          askTrash: (ids) => ids.length && setTrashing(ids),
          askStructure: setStructureAsk,
          reveal: (target) => {
            // The editor keeps the page it has when the passage is on it; otherwise it opens the passage's document
            // (a scene in Write, a note in Research).
            if (editing && shownFragments.includes(target.fragmentId)) {
              setRevealing({ ...target });
              return;
            }
            const here = nodeOfFragment(tree, target.fragmentId);
            const manuscriptId = project.manuscriptId();
            const there = !here && manuscriptId ? nodeOfFragment(project.tree(manuscriptId), target.fragmentId) : null;
            // Neither here nor in the manuscript (a note in Research & Notes): whichever arrangement has it.
            let elsewhere: { arrangementId: string; nodeId: string } | null = null;
            if (!here && !there) {
              for (const a of arrangements) {
                const n = nodeOfFragment(project.tree(a.id), target.fragmentId);
                if (n) {
                  elsewhere = { arrangementId: a.id, nodeId: n.id };
                  break;
                }
              }
            }
            setRevealing({ ...target });
            const to = (nodeId: string, arrangementId: string | null) => {
              const [room, pg] = editorFor(arrangementId).split('/') as [RoomId, string];
              navigate(formatRoute({ name: 'project', projectId: route.projectId, room, page: pg, nodeId, arrangementId, params: {} }));
            };
            if (here) navigate(hrefFor(editorFor(arrangement.id), here.id));
            else if (there) to(there.id, null);
            else if (elsewhere) to(elsewhere.nodeId, elsewhere.arrangementId === manuscriptId ? null : elsewhere.arrangementId);
          },
          revealing,
          clearReveal: () => setRevealing(null),
          openSheet: setSheet,
          writing,
          shownFragments,
          setShownFragments: (ids) => setShownFragments((old) => (old.length === ids.length && old.every((x, i) => x === ids[i]) ? old : ids)),
        }
      : null;
  const workspaceRef = useRef<Workspace | null>(null);
  workspaceRef.current = workspace;
  // The contents' own props, stable across rebuilds (it's memoized).
  const treeHref = useCallback((n: TreeNode) => hrefFor(pageForNode, n.id), [route.projectId, arrangement?.id, inManuscript, pageForNode]); // eslint-disable-line react-hooks/exhaustive-deps
  const treeChoose = useCallback((n: TreeNode) => {
    setDrawer(false);
    navigate(hrefFor(pageForNode, n.id));
  }, [route.projectId, arrangement?.id, inManuscript, pageForNode]); // eslint-disable-line react-hooks/exhaustive-deps
  // The inspector's fragment: the caret's on Write, else the page's item's.
  const inspected = (editing && caretFragment && project?.fragment(caretFragment) ? caretFragment : null) ?? node?.fragmentId ?? null;
  // Pages about an item have the inspector; so does any page while its Look Up tab is chosen (the answer isn't about the page's item).
  const showsInspector = inspectorOpen && (page.takesNode || lookUpTab === 'lookUp') && !!project;
  const showsColumn = phone ? drawer : columnPref;

  // The keys (see the header). Read through a ref: the listener is added once.
  const keysRef = useRef({ inspectorOpen, setInspectorOpen, canWrite, roomHref, phone, drawer, columnPref, setColumnPref, takesNode: page.takesNode, filter });
  keysRef.current = { inspectorOpen, setInspectorOpen, canWrite, roomHref, phone, drawer, columnPref, setColumnPref, takesNode: page.takesNode, filter };
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      const k = keysRef.current;
      const mod = e.metaKey || e.ctrlKey;
      const toRoom = roomForKey(e);
      if (toRoom) {
        e.preventDefault();
        navigate(k.roomHref(toRoom));
      } else if (isColumnKey(e)) {
        e.preventDefault();
        if (k.phone) setDrawer(!k.drawer);
        else k.setColumnPref(!k.columnPref);
      } else if (mod && e.altKey && e.code === 'KeyI') {
        e.preventDefault();
        if (k.takesNode) k.setInspectorOpen(!k.inspectorOpen);
      } else if (mod && !e.altKey && !e.shiftKey && e.code === 'KeyS') {
        e.preventDefault();
        if (k.canWrite) setSheet('checkpoint');
      } else if (mod && e.shiftKey && !e.altKey && e.code === 'KeyF') {
        e.preventDefault();
        setSheet('find');
      } else if (mod && e.altKey && !e.shiftKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp') && !e.defaultPrevented) {
        // Next and Previous Document (through the filter's matches); the contents' own ⌥⌘↑/↓ move rows instead.
        e.preventDefault();
        k.filter?.step(e.key === 'ArrowDown');
      }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, []);

  // Page changes move focus to the page's heading (and say where it went).
  const firstRender = useRef(true);
  useEffect(() => {
    if (!project) return;
    const heading = pageRef.current?.querySelector<HTMLElement>('h1');
    const title = heading?.textContent ?? project.title;
    document.title = `${title} — Five Rooms`;
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    // A new item being named keeps the focus in its title field.
    if (renaming) return;
    heading?.focus({ preventScroll: true });
    pageRef.current?.scrollTo?.({ top: 0 });
    window.scrollTo?.({ top: 0 });
    announce(title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.room, route.page, route.nodeId, arrangement?.id, !!project]);

  const newItem = (folder: boolean) => {
    if (!workspace) return;
    const id = structureEdits(workspace).newItem(route.nodeId && node ? node.id : null, folder);
    if (!id) return;
    setRenaming(id);
    navigate(hrefFor(folder && editing ? `${route.room}/${page.page}` : pageForNode, id));
  };

  const listed = app.projects?.find((p) => p.id === route.projectId);
  const title = listed ? projectTitle(listed) : (project?.title ?? 'Project');
  const offline = !app.online || open.connection === 'offline';
  // A journal has no beat board and none of fiction's story pages (`LibraryModel.pages(of:)`).
  const tabs = reg.tabs(route.room).filter((t) => !(project?.type === 'journal' && t.room === 'plan' && JOURNAL_HIDDEN_PAGES.has(t.page)));
  const pageProps: PageProps | null =
    project && arrangement ? { projectId: route.projectId, project, tree, node, arrangement, hrefFor, live: open.live, page, params: route.params } : null;
  const [roomSheetId, setRoomSheetId] = useState<string | null>(null);
  const menuContext: MenuContext | null = pageProps ? { ...pageProps, canWrite, canStructure, navigate, openRoomSheet: setRoomSheetId } : null;
  const RoomSheet = roomSheetId ? roomSheet(roomSheetId) : undefined;
  const inLibrary = route.room === 'library';
  const helpHref = page.help ? `/help/${page.help}` : null;
  const HeaderControls = page.headerControls;
  const OwnColumn = page.column;
  const PageComponent = page.component;
  const columnTitle = page.columnTitle ?? room.columnTitle ?? 'Shelf';

  const contents =
    project && arrangement ? (
      <>
        <SourcePicker
          projectId={route.projectId}
          pageForNode={pageForNode}
          room={route.room}
          arrangements={either ? arrangements : sideArrangements}
          onArrangement={(a) => {
            const to = pageForNode.split('/');
            navigate(formatRoute({ name: 'project', projectId: route.projectId, room: to[0] as RoomId, page: to[1], nodeId: null, arrangementId: a.id === project.manuscriptId() ? null : a.id, params: {} }));
          }}
        />
        <FilterBar />
        {canStructure ? (
          <div className="tree-tools" role="toolbar" aria-label="Shelf">
            <button type="button" className="quiet-button" onClick={() => newItem(false)} title={`${project.type === 'journal' && arrangement.role === 'manuscript' ? 'New post' : 'New scene'} after the selection`}>
              {project.type === 'journal' && arrangement.role === 'manuscript' ? 'New post' : 'New scene'}
            </button>
            <button type="button" className="quiet-button" onClick={() => newItem(true)}>
              New folder
            </button>
          </div>
        ) : null}
        <a
          className={`tree-root${route.nodeId ? '' : ' selected'}`}
          href={hrefFor(pageForNode, null)}
          aria-current={route.nodeId ? undefined : 'true'}
          onClick={(e) => {
            setDrawer(false);
            followLink(e, hrefFor(pageForNode, null));
          }}
        >
          {inManuscript ? 'The whole book' : `All of ${arrangement.name}`}
        </a>
        {filtering && filter?.state.list ? (
          <FilterList tree={tree} selected={route.nodeId} hrefFor={treeHref} onChoose={treeChoose} />
        ) : (
          <ContentsTree
            key={arrangement.id}
            tree={dims ? fullTree : tree}
            fullTree={fullTree}
            dims={dims}
            facts={rowFacts}
            inManuscript={inManuscript}
            selected={page.takesNode ? route.nodeId : null}
            label={arrangement.name}
            hrefFor={treeHref}
            onChoose={treeChoose}
            editable={canStructure}
            locked={locks}
            numbers={numbers}
            renaming={renamingState?.in === 'contents' ? renaming : null}
            filtered={filter?.view ?? null}
          />
        )}
        <FilterFoot />
      </>
    ) : null;

  const column = OwnColumn && pageProps ? (
    <OwnColumn {...pageProps} />
  ) : room.column === 'projects' ? (
    <ProjectsColumn currentId={route.projectId} page={route.page} onChoose={() => setDrawer(false)} />
  ) : (
    contents
  );

  return (
    <WorkspaceRefContext.Provider value={workspaceRef}>
    <WorkspaceContext.Provider value={workspace}>
    <div
      className={`shell${drawer ? ' drawer-open' : ''}${showsInspector && !narrow ? ' with-inspector' : ''}${showsColumn ? '' : ' without-column'}`}
      data-room={route.room}
      data-page={route.page}
      data-state="project"
      data-connection={open.connection}
      data-ready-ms={open.readyMs ?? ''}
      data-cache-ms={open.timings.cacheMs ?? ''}
      data-network-ms={open.timings.networkMs ?? ''}
      data-cached={open.timings.cached}
      data-fetched={open.timings.fetched}
    >
      <a className="skip-link" href="#page">
        Skip to the page
      </a>
      <header className="topbar">
        <div className="topbar-lead">
        <button
          ref={toggleRef}
          type="button"
          className="drawer-toggle"
          aria-expanded={phone ? drawer : columnPref}
          aria-controls="contents"
          title={`Show or hide the column (${navigator.platform?.startsWith('Mac') ? '⌃⌘S' : 'Ctrl+Alt+S'})`}
          onClick={() => {
            if (!phone) return setColumnPref(!columnPref);
            setDrawer(!drawer);
            if (!drawer) requestAnimationFrame(() => document.querySelector<HTMLElement>('#contents [role="treeitem"][tabindex="0"], #contents a')?.focus());
          }}
        >
          {columnTitle}
        </button>
        <a className="topbar-title" href={inLibrary ? backHref : libraryHref} onClick={(e) => followLink(e, inLibrary ? backHref : libraryHref)}>
          {title}
        </a>
        </div>
        <nav className="room-tabs" aria-label="Rooms">
          {WORKING_ROOMS.map((id) => {
            const r = roomDef(id);
            const href = roomHref(id);
            return (
              <a
                key={id}
                href={href}
                aria-current={route.room === id ? 'page' : undefined}
                aria-keyshortcuts={`Control+Alt+${r.key} Meta+Alt+${r.key}`}
                title={`${r.title} (⌥⌘${r.key})`}
                data-room={id}
                onClick={(e) => followLink(e, href)}
              >
                {r.title}
              </a>
            );
          })}
        </nav>
        <div className="topbar-trailing">
          <OfflineNote live={open.live} offline={offline && !!project} />
          {open.epochNotice ? (
            <p className="status-note epoch-note" role="status" data-epoch-notice={open.epochNotice.doc} title={open.epochNotice.message}>
              History trimmed on another device
            </p>
          ) : null}
          {rotation ? (
            <p className="status-note rotation-note" role="status" data-rotation={rotation.step}>
              {rotationText(rotation)}
            </p>
          ) : null}
          {role !== 'owner' ? (
            <p className="status-note shared-note" data-role={role} title={ownerName ? `Shared by ${ownerName}` : undefined}>
              {role === 'commenter' ? 'Commenting' : 'Co-writing'}
              {ownerName ? ` · ${ownerName}’s` : ''}
            </p>
          ) : null}
          <SaveNote live={open.live} offline={offline} />
          {project ? (
            <MenuButton
              label="Project"
              className="quiet-button project-menu"
              menuLabel="Project"
              entries={() => [
                { label: 'Create Checkpoint…', shortcut: '⌘S', disabled: !canWrite, onSelect: () => setSheet('checkpoint') },
                { label: 'Find in Project…', shortcut: '⇧⌘F', onSelect: () => setSheet('find') },
                { label: 'Next Document', shortcut: '⌥⌘↓', disabled: !filter, onSelect: () => filter?.step(true) },
                { label: 'Previous Document', shortcut: '⌥⌘↑', disabled: !filter, onSelect: () => filter?.step(false) },
                ...(node && filter && (node.isFolder || node.children.length > 0)
                  ? [{ label: `Focus on “${node.title || 'Untitled'}”`, onSelect: () => filter.focus(node.id) }]
                  : []),
                { label: 'Clear Filter', disabled: !filter?.active && !filter?.state.filter, onSelect: () => filter?.clear() },
                ...(role === 'owner' ? [{ label: 'Import from Scrivener Again…', disabled: !writable || !open.live, onSelect: () => setSheet('reimport') }] : []),
                { label: 'Import Scapple Board…', disabled: !writable || !open.live || role === 'commenter', onSelect: scapple.start },
                { label: 'Turn Link Comments into Links', disabled: !writable || !open.live || role === 'commenter', onSelect: turnLinkCommentsIntoLinks },
                // The writer's Notes for Assistants (AgentBrief): made at the top of Research & Notes on first use, then opened.
                {
                  label: 'Notes for Assistants…', disabled: !canWrite || !open.live,
                  onSelect: () => {
                    if (!open.live) return;
                    const made = open.live.edit((editor) => {
                      const d = editor as unknown as DocumentEditor;
                      return new StoreEditor(d.docs, d.context).ensureAgentBrief();
                    });
                    const [room, pg] = editorFor(made.arrangementId).split('/') as [RoomId, string];
                    navigate(formatRoute({ name: 'project', projectId: route.projectId, room, page: pg, nodeId: made.nodeId, arrangementId: made.arrangementId, params: {} }));
                  },
                },
                // A script's layout: a screenplay, a sitcom, a play in US or UK format or a radio play, and for a play,
                // Musical (Project ▸ Script Format on the Mac).
                ...(project.type === 'screenplay'
                  ? [
                      { kind: 'separator' as const },
                      { kind: 'heading' as const, label: 'Script Format' },
                      ...SCRIPT_FORMATS.map((f) => ({
                        kind: 'radio' as const, label: scriptFormatName(f), checked: project.scriptFormat === f, disabled: !canWrite || !open.live,
                        onSelect: () => { if (open.live) planEdit(open.live, (e) => e.setScriptFormat(f)); },
                      })),
                      ...(project.kind === 'stage-play'
                        ? [{
                            kind: 'check' as const, label: 'Musical', checked: project.musical, disabled: !canWrite || !open.live,
                            onSelect: () => { if (open.live) planEdit(open.live, (e) => e.setMusical(!project.musical)); },
                          }]
                        : []),
                    ]
                  : []),
                { kind: 'separator' },
                writing?.state.sprint
                  ? { label: 'Finish Writing Session', onSelect: () => void writing.finish() }
                  : { label: 'Start Writing Session…', disabled: !canWrite, onSelect: () => setSheet('sprint') },
                { label: 'Count From Now', disabled: !writing, onSelect: () => writing?.newSession() },
                { kind: 'separator' },
                { label: 'History', onSelect: () => navigate(hrefFor('revise/history', null)) },
                { label: 'Comments', onSelect: () => navigate(hrefFor('revise/comments', null)) },
                ...(role === 'owner' ? [{ label: 'Readers', onSelect: () => navigate(hrefFor('revise/readers', null)) }] : []),
                { label: 'Publish', onSelect: () => navigate(hrefFor('publish/publish', null)) },
                { label: 'Library', shortcut: '⌥⌘0', onSelect: () => navigate(libraryHref) },
                { kind: 'separator' },
                ...(menuContext ? roomMenuEntries(menuContext) : []),
                { kind: 'separator' },
                ...(role === 'owner'
                  ? [
                      { label: 'Share Project…', disabled: !app.online || !listedEntry, onSelect: () => setSharingOpen(true) },
                      { label: 'Agents on the Web…', onSelect: () => navigate(hrefFor('library/agents', null)) },
                    ]
                  : [{ label: 'Leave Project…', disabled: !app.online, onSelect: () => setLeaving(true) }]),
                { label: 'Five Rooms Help', onSelect: () => navigate(`/help/${page.help ?? room.help}`) },
              ]}
            >
              Project
            </MenuButton>
          ) : null}
          <AccountLinks />
        </div>
      </header>
      <aside
        className="column"
        id="contents"
        aria-label={columnTitle}
        hidden={!phone && !columnPref}
        onKeyDown={(e) => {
          if (e.key !== 'Escape' || !drawer) return;
          setDrawer(false);
          toggleRef.current?.focus();
        }}
      >
        <a
          className="project-name"
          href={inLibrary ? backHref : libraryHref}
          title={inLibrary ? 'Back to the open project' : 'Your projects and progress (⌥⌘0)'}
          onClick={(e) => {
            setDrawer(false);
            followLink(e, inLibrary ? backHref : libraryHref);
          }}
        >
          <span className="project-name-title">{inLibrary ? 'Library' : title}</span>
          {inLibrary ? null : <span className="project-name-library">Library</span>}
        </a>
        {column}
      </aside>
      <div className="drawer-scrim" hidden={!drawer} onClick={() => setDrawer(false)} />
      <main className="panel" id="page" ref={pageRef} tabIndex={-1}>
        <QuotaBanner live={open.live} />
        {closedShare && listedEntry ? <RemovedBanner project={listedEntry} /> : null}
        <header className="panel-head room-header">
          {tabs.length > 1 ? (
            <nav className="page-tabs" aria-label={`${room.title} pages`}>
              {tabs.filter((p) => !p.group).map((p) => {
                const href = hrefFor(`${p.room}/${p.page}`, p.takesNode ? route.nodeId : null);
                return (
                  <a
                    key={p.page}
                    href={href}
                    aria-current={p.page === page.page ? 'page' : undefined}
                    data-page={p.page}
                    data-placeholder={p.placeholder || undefined}
                    onClick={(e) => followLink(e, href)}
                  >
                    {p.title}
                  </a>
                );
              })}
              {tabGroups(tabs).map(([group, pages]) => {
                // One tab for the group, reading the chosen page's name when one of them is showing, with a menu of them.
                const chosen = pages.find((p) => p.page === page.page) ?? null;
                return (
                  <MenuButton
                    key={group}
                    className={`tab-group${chosen ? ' current' : ''}`}
                    label={group}
                    menuLabel={`${group} pages`}
                    entries={pages.map((p) => ({
                      kind: 'radio' as const,
                      label: p.title,
                      checked: p === chosen,
                      onSelect: () => navigate(hrefFor(`${p.room}/${p.page}`, p.takesNode ? route.nodeId : null)),
                    }))}
                  >
                    <span data-page={chosen?.page} aria-current={chosen ? 'page' : undefined}>{chosen ? chosen.title : group}</span>
                    <span className="tab-group-chevron" aria-hidden="true">▾</span>
                  </MenuButton>
                );
              })}
            </nav>
          ) : null}
          <div className="header-controls">
            {HeaderControls && pageProps ? <HeaderControls {...pageProps} /> : null}
            {page.takesNode && project ? (
              <button
                type="button"
                className="quiet-button inspector-toggle"
                aria-pressed={inspectorOpen}
                aria-controls={inspectorOpen ? 'inspector' : undefined}
                title="Inspector (⌥⌘I)"
                onClick={() => setInspectorOpen(!inspectorOpen)}
              >
                Inspector
              </button>
            ) : null}
            {helpHref ? (
              <a className="help-link" href={helpHref} title={`Help: ${page.title}`} aria-label={`Help for ${page.title}`} onClick={(e) => followLink(e, helpHref)}>
                ?
              </a>
            ) : null}
          </div>
        </header>
        <div className="panel-body">
          {pageProps ? (
            route.nodeId && !node ? (
              <NotHere hrefFor={hrefFor} />
            ) : (
              <PageComponent key={`${page.room}/${page.page}`} {...pageProps} />
            )
          ) : (
            <ProjectPending open={open} title={title} offline={offline} />
          )}
        </div>
      </main>
      {showsInspector && project ? (
        <Inspector
          project={project}
          live={open.live}
          fragmentId={inspected}
          canWrite={canWrite}
          mode={narrow ? 'sheet' : 'panel'}
          onClose={() => setInspectorOpen(false)}
        />
      ) : null}
      {sheet === 'checkpoint' && workspace ? (
        <TextDialog
          title="Make a Checkpoint"
          label="Name"
          initial={new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date())}
          confirm="Make"
          message={<p>Records the whole book as it is now, so you can compare with it later (and go back to it on your Mac).</p>}
          onCancel={() => setSheet(null)}
          onDone={(name) => {
            setSheet(null);
            open.live?.edit((e) => e.createCheckpoint(name));
            announce(`Checkpoint “${name}” made`);
          }}
        />
      ) : null}
      {sheet === 'find' && workspace ? <FindPanel workspace={workspace} narrow={narrow} onClose={() => setSheet(null)} /> : null}
      {sheet === 'sprint' && writing ? <SprintDialog writing={writing} onClose={() => setSheet(null)} /> : null}
      {sheet === 'reimport' && open.live ? (
        <ScrivenerReimportDialog live={open.live} projectId={route.projectId} projectKey={listedEntry?.key ?? null} author={accountName ?? 'Me'} onClose={() => setSheet(null)} />
      ) : null}
      {scapple.element}
      {RoomSheet && menuContext ? <RoomSheet {...menuContext} onClose={() => setRoomSheetId(null)} /> : null}
      {writing ? <SprintSummary writing={writing} /> : null}
      {sharingOpen && listedEntry ? <ShareDialog project={listedEntry} live={open.live} onClose={() => setSharingOpen(false)} /> : null}
      {leaving && listedEntry ? <LeaveDialog project={listedEntry} onClose={() => setLeaving(false)} /> : null}
      {offeringLinks !== null ? (
        <ConfirmDialog
          title="Turn link comments into links?"
          message={
            <p>
              {offeringLinks === 1 ? '1 link' : `${offeringLinks} links`} in this project came in as margin comments, from before Five Rooms had links. They
              can be links on the words they mark. A checkpoint is made first; you can also do this later from the Project menu.
            </p>
          }
          confirm="Turn into Links"
          onCancel={() => setOfferingLinks(null)}
          onConfirm={() => {
            setOfferingLinks(null);
            turnLinkCommentsIntoLinks();
          }}
        />
      ) : null}
      {structureAsk ? (
        <ConfirmDialog
          title={structureAsk.title}
          message={structureAsk.message ? <p>{structureAsk.message}</p> : null}
          confirm={structureAsk.confirm ?? 'OK'}
          cancel={structureAsk.onConfirm ? 'Cancel' : null}
          onCancel={() => setStructureAsk(null)}
          onConfirm={() => {
            const ask = structureAsk;
            setStructureAsk(null);
            ask.onConfirm?.();
          }}
        />
      ) : null}
      {trashing && workspace ? (
        <ConfirmDialog
          title={trashing.length === 1 ? `Move “${locate(tree, trashing[0])?.node.title || 'Untitled'}” to the Trash?` : `Move ${trashing.length} items to the Trash?`}
          message={<p>{trashTally(tree, trashing)} Nothing is deleted: the Trash keeps it, and you can open the Trash from the arrangement menu.</p>}
          confirm="Move to Trash"
          onCancel={() => setTrashing(null)}
          onConfirm={() => {
            const ids = trashing;
            setTrashing(null);
            const leaving = !!route.nodeId && ids.some((id) => isWithin(tree, route.nodeId!, id));
            structureEdits(workspace).trash(ids);
            if (leaving) navigate(hrefFor(pageForNode, null), { replace: true });
            announce(ids.length === 1 ? 'Moved to the Trash' : `Moved ${ids.length} items to the Trash`);
          }}
        />
      ) : null}
    </div>
    </WorkspaceContext.Provider>
    </WorkspaceRefContext.Provider>
  );
}

/** This device's edits on their way (its own subscription: the status changes with every keystroke). */
function useSave(live: LiveProject | null): OutboxStatus | null {
  return useSyncExternalStore(
    (fn) => live?.onSave(fn) ?? (() => {}),
    () => live?.state.save ?? null,
  );
}

function SaveNote({ live, offline }: { live: LiveProject | null; offline: boolean }) {
  const save = useSave(live);
  const saving = saveState(save, !offline);
  return (
    <p
      className="status-note save-note"
      role="status"
      data-save-state={saving?.state ?? 'idle'}
      data-pending={save ? save.pending + save.unsaved : 0}
      data-last-sent-at={save?.lastSentAt ?? ''}
      data-last-ack-at={save?.lastAckAt ?? ''}
      title={saving?.detail}
    >
      {saving?.text ?? ''}
    </p>
  );
}

/** A save the relay refused because the account is full: what it means and what to do, above the page. */
function QuotaBanner({ live }: { live: LiveProject | null }) {
  const save = useSave(live);
  const saving = saveState(save, true);
  if (saving?.state !== 'quota') return null;
  return (
    <div className="quota-banner" role="alert" data-quota-banner>
      <p>{saving.detail}</p>
      <p className="quota-banner-links">
        <a href="/export" onClick={(e) => followLink(e, '/export')}>
          Export projects…
        </a>
        <a href="/account">Your account</a>
      </p>
    </div>
  );
}

function OfflineNote({ live, offline }: { live: LiveProject | null; offline: boolean }) {
  const save = useSave(live);
  if (!offline || saveState(save, false)?.state === 'kept') return null;
  return (
    <p className="status-note" role="status" data-offline>
      Offline — showing what this computer has
    </p>
  );
}

/** Two trees with the same nodes, titles and order. */
export function sameTree(a: TreeNode[], b: TreeNode[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i], y = b[i];
    if (x.id !== y.id || x.fragmentId !== y.fragmentId || x.title !== y.title || x.isFolder !== y.isFolder || !sameTree(x.children, y.children)) return false;
  }
  return true;
}

/** What moving these to the Trash takes with it, in words. */
function trashTally(tree: TreeNode[], ids: string[]): string {
  const inside = fragmentsOf(tree, ids.flatMap((id) => {
    const all: string[] = [];
    const walk = (n: TreeNode) => { all.push(n.id); n.children.forEach(walk); };
    const at = locate(tree, id)?.node;
    if (at) walk(at);
    return all;
  })).length;
  return inside > ids.length ? `It takes ${inside} documents with it.` : '';
}

/** Whether a media query matches, following it. */
function useNarrow(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof matchMedia !== 'undefined' && matchMedia(query).matches);
  useEffect(() => {
    const m = matchMedia(query);
    const on = () => setMatches(m.matches);
    on();
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, [query]);
  return matches;
}

function NotHere({ hrefFor }: { hrefFor: PageProps['hrefFor'] }) {
  return (
    <div className="page-message">
      <h1 tabIndex={-1}>Not in this arrangement</h1>
      <p className="lede">
        That item isn’t here any more. <a href={hrefFor('write', null)}>Open the whole book</a> instead.
      </p>
    </div>
  );
}

function ProjectPending({ open, title, offline }: { open: ReturnType<typeof useLiveProject>; title: string; offline: boolean }) {
  if (open.missing) {
    return (
      <div className="page-message">
        <h1 tabIndex={-1}>Not on the web</h1>
        <p className="lede">This project isn’t among the ones you’ve put on the web.</p>
      </div>
    );
  }
  if (open.status === 'failed') {
    return (
      <div className="page-message">
        <h1 tabIndex={-1}>{title}</h1>
        <p className="error" role="alert">
          This project couldn’t be opened. {open.error}
        </p>
      </div>
    );
  }
  if (open.status === 'empty' && offline) {
    return (
      <div className="page-message">
        <h1 tabIndex={-1}>{title}</h1>
        <p className="lede">This computer hasn’t kept this project yet. Connect to the internet to read it; from then on it reads offline too.</p>
      </div>
    );
  }
  return (
    <div className="page-message" aria-busy="true">
      <h1 tabIndex={-1}>{title}</h1>
      <p className="lede">Opening…</p>
    </div>
  );
}
